import { z } from "zod";
/**
 * Recipient validation lives here and ONLY here (SPEC §10: "Recipient
 * validation rules must not exist in two places"). The API handler and the
 * SPA both import these.
 */
export const MAX_RECIPIENTS = 20;
export const MIN_RECIPIENTS = 1;
export const MAX_ADDRESS_LENGTH = 254;
/**
 * RFC-5322-ish. Deliberately not the full grammar — the full grammar accepts
 * addresses no mail system in practice will route, and rejecting a valid-but-
 * exotic address is a far cheaper failure here than accepting a typo.
 */
const ADDRESS_RE = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;
export function isValidAddress(raw) {
    const value = raw.trim();
    if (value.length === 0 || value.length > MAX_ADDRESS_LENGTH)
        return false;
    return ADDRESS_RE.test(value);
}
/**
 * Validate, then lowercase and dedupe. Order matters: dedupe runs after
 * lowercasing so `A@x.com` and `a@x.com` collapse to one address, and the
 * count limits are checked against the deduped list.
 */
export function normalizeRecipients(input) {
    if (!Array.isArray(input)) {
        return { ok: false, details: ["'recipients' must be an array of strings"] };
    }
    const details = [];
    const seen = new Set();
    const recipients = [];
    for (const entry of input) {
        if (typeof entry !== "string") {
            details.push(`${JSON.stringify(entry)} is not a string`);
            continue;
        }
        const trimmed = entry.trim();
        if (trimmed.length > MAX_ADDRESS_LENGTH) {
            details.push(`'${trimmed.slice(0, 40)}…' exceeds ${MAX_ADDRESS_LENGTH} characters`);
            continue;
        }
        if (!isValidAddress(trimmed)) {
            details.push(`'${trimmed}' is not a valid address`);
            continue;
        }
        const lowered = trimmed.toLowerCase();
        if (seen.has(lowered))
            continue;
        seen.add(lowered);
        recipients.push(lowered);
    }
    if (details.length > 0)
        return { ok: false, details };
    if (recipients.length < MIN_RECIPIENTS) {
        return {
            ok: false,
            details: [
                "at least one recipient is required — use the cutoff switch to stop sending",
            ],
        };
    }
    if (recipients.length > MAX_RECIPIENTS) {
        return { ok: false, details: [`at most ${MAX_RECIPIENTS} recipients are allowed`] };
    }
    return { ok: true, recipients };
}
/** Zod wrapper over the same rules, for schema-level parsing. */
export const recipientsSchema = z
    .array(z.string())
    .superRefine((value, ctx) => {
    const result = normalizeRecipients(value);
    if (!result.ok) {
        for (const message of result.details) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message });
        }
    }
})
    .transform((value) => {
    const result = normalizeRecipients(value);
    return result.ok ? result.recipients : value;
});
//# sourceMappingURL=recipients.js.map